import { Button, Stack, Text, Box } from '@stitch-fix/mode-react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import styles from './styles.module.scss';
import { ControlledTextField } from '../Forms/ControlledTextField';
import { ControlledSingleSelectButtonGroup } from '../Forms/ControlledSingleSelectButtonGroup';
import { ReturnExchangeItem } from './ReturnExchangeItem';
const stringEmptyDefault = z.string().optional().default('');
const feedbackFormSchema = z.object({
  multiple_choice_size: stringEmptyDefault,
  multiple_choice_fit_cut: stringEmptyDefault,
  multiple_choice_style: stringEmptyDefault,
  multiple_choice_quality: stringEmptyDefault,
  multiple_choice_price: stringEmptyDefault,
  other_comments: stringEmptyDefault
});
export type InputFormNameValues = 'other_comments' | 'multiple_choice_fit_cut' | 'multiple_choice_price' | 'multiple_choice_quality' | 'multiple_choice_size' | 'multiple_choice_style';
type FeedbackFormInputSchema = z.input<typeof feedbackFormSchema>;
type FeedbackFormOutputSchema = z.infer<typeof feedbackFormSchema>;
type CommentQuestion = {
  questionId: string;
  value: string;
};
export type KeptItemFeedbackMultipleChoice = {
  questionId: InputFormNameValues;
  label: string;
  options: Option[];
};
export type Option = {
  label: string;
  questionId: string;
  value: boolean;
};
export type FeedbackFormInputValues = {
  multiple_choice_fit_cut: string;
  multiple_choice_price: string;
  multiple_choice_quality: string;
  multiple_choice_size: string;
  multiple_choice_style: string;
  other_comments: string;
};

/**
 * If an option is selected, it will return that questionID's value,
 * an empty string if there is no selected option
 */
const getFeedbackQuestionValue = (questionId: string, multipleChoiceQuestions: KeptItemFeedbackMultipleChoice[]) => {
  const questionValue = multipleChoiceQuestions.find(question => question.questionId === questionId);
  if (questionValue && questionValue.options) {
    const selectedOption = questionValue.options.find((option: Option) => option.value === true);
    return selectedOption ? selectedOption.questionId : '';
  }
  return '';
};

/**
 * prepares button select options for consumption by the ControlledSingleSelectButtonGroup
 * mode react component
 */
const formattedOptions = (options: Option[]) => {
  return options.map(option => {
    return {
      value: option.questionId,
      label: option.label
    };
  });
};

/**
 * Reduces multipleChoiceQuestions to the key - the feedback question, and the value -
 * the current selected option. Assigns the reduced questions as well as the
 * comment question to an object consumable by `control` in our react-hook-form
 */
const buildFormDefaultValues = ({
  multipleChoiceQuestions,
  commentQuestion
}: {
  multipleChoiceQuestions: KeptItemFeedbackMultipleChoice[];
  commentQuestion: CommentQuestion;
}) => {
  const multipleChoiceValues = multipleChoiceQuestions.reduce((acc, questionValue) => {
    const questionId = questionValue.questionId;
    return {
      ...acc,
      [questionId]: getFeedbackQuestionValue(questionId, multipleChoiceQuestions)
    };
  }, {});
  return {
    ...multipleChoiceValues,
    other_comments: commentQuestion.value || ''
  };
};

/**
 *
 * The form section of the Bought Items feedback modal
 */
export const FeedbackForm = ({
  multipleChoiceQuestions,
  commentQuestion,
  onUpdate,
  eligibleForReturn,
  returnHeading,
  returnBody,
  orderId,
  returnInProgress
}: {
  multipleChoiceQuestions: KeptItemFeedbackMultipleChoice[];
  commentQuestion: CommentQuestion;
  onUpdate: (inputData: FeedbackFormInputValues) => void;
  eligibleForReturn: boolean;
  returnHeading: string;
  returnBody: string;
  orderId: string;
  returnInProgress?: boolean;
}) => {
  const {
    control,
    handleSubmit
  } = useForm<FeedbackFormInputSchema, unknown, FeedbackFormOutputSchema>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: buildFormDefaultValues({
      multipleChoiceQuestions,
      commentQuestion
    })
  });
  return <form onSubmit={handleSubmit(onUpdate)}>
      <Stack mt={1} spacing={1.75}>
        {multipleChoiceQuestions.map(question => <React.Fragment key={question.questionId}>
            <Text setting="title-small">{question.label}</Text>
            <Box mt={1} className={styles.fitButtonGroup}>
              <ControlledSingleSelectButtonGroup control={control} name={question.questionId} options={formattedOptions(question.options)} />
            </Box>
          </React.Fragment>)}
        <ControlledTextField rows={2} control={control} label="Other comments?" name="other_comments" />
      </Stack>
      {eligibleForReturn && <ReturnExchangeItem returnHeading={returnHeading} returnBody={returnBody} orderId={orderId} returnInProgress={returnInProgress} />}
      <Button mt={0.5} type="submit">
        Save
      </Button>
    </form>;
};